import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface BookmarkProduct {
  id: string;
  product_id: string;
  product: {
    id: string;
    title: string;
    type: string;
    price: number;
    thumbnail_url: string | null;
    visibility: string;
    provider?: { name: string; service_name: string | null };
  } | null;
  created_at: string;
}

interface BookmarkProvider {
  id: string;
  provider_id: string;
  provider: {
    id: string;
    name: string;
    service_name: string | null;
    avatar_url: string | null;
    category: string | null;
    rating: number | null;
  } | null;
  created_at: string;
}

export default function BookmarkList() {
  const { currentUser } = useApp();
  const [productBookmarks, setProductBookmarks] = useState<BookmarkProduct[]>([]);
  const [providerBookmarks, setProviderBookmarks] = useState<BookmarkProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"products" | "providers">("products");

  useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    const load = async () => {
      const [{ data: products }, { data: providers }] = await Promise.all([
        supabase
          .from("bookmarks")
          .select("id, product_id, created_at, products:product_id(id, title, type, price, thumbnail_url, visibility, profiles:provider_id(name, service_name))")
          .eq("user_id", currentUser.id)
          .eq("type", "product")
          .order("created_at", { ascending: false }),
        supabase
          .from("bookmarks")
          .select("id, provider_id, created_at, profiles:provider_id(id, name, service_name, avatar_url, category, rating)")
          .eq("user_id", currentUser.id)
          .eq("type", "provider")
          .order("created_at", { ascending: false }),
      ]);

      const pb = (products || []).map((b: { id: string; product_id: string; created_at: string; products: unknown }) => ({
        ...b,
        product: Array.isArray(b.products) ? b.products[0] ?? null : (b.products as BookmarkProduct["product"] | null),
      }));
      const provb = (providers || []).map((b: { id: string; provider_id: string; created_at: string; profiles: unknown }) => ({
        ...b,
        provider: Array.isArray(b.profiles) ? b.profiles[0] ?? null : (b.profiles as BookmarkProvider["provider"] | null),
      }));

      setProductBookmarks(pb as BookmarkProduct[]);
      setProviderBookmarks(provb as BookmarkProvider[]);
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  const removeBookmark = async (id: string, type: "products" | "providers") => {
    await supabase.from("bookmarks").delete().eq("id", id);
    if (type === "products") setProductBookmarks((prev) => prev.filter((b) => b.id !== id));
    else setProviderBookmarks((prev) => prev.filter((b) => b.id !== id));
  };

  if (loading) return <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-display italic text-gold text-xs tracking-widest mb-1">My Collections</p>
          <h3 className="font-serif text-deep-green text-xl font-light">あとで見る・コレクション</h3>
        </div>
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
          {([{ v: "products" as const, l: `講座・教材 (${productBookmarks.length})` }, { v: "providers" as const, l: `事業者 (${providerBookmarks.length})` }]).map((t) => (
            <button
              key={t.v}
              onClick={() => setActiveTab(t.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${activeTab === t.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "products" ? (
        productBookmarks.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-100 py-16 text-center">
            <i className="ri-bookmark-line text-5xl text-charcoal/20 block mb-4" />
            <p className="font-serif text-charcoal/40 text-lg">保存した講座・教材がありません</p>
            <p className="text-sm text-charcoal/30 font-sans mt-2">気になる講座にブックマークボタンを押して保存しましょう</p>
            <Link to="/courses" className="mt-4 inline-block text-teal text-sm font-sans hover:underline cursor-pointer">講座を探す →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {productBookmarks.map((b) => {
              const p = b.product;
              if (!p) return null;
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                  <div className="relative h-36">
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt={p.title} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                        <i className={`${p.type === "video" ? "ri-video-line" : "ri-box-3-line"} text-3xl text-charcoal/20`} />
                      </div>
                    )}
                    <button
                      onClick={() => removeBookmark(b.id, "products")}
                      className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-gold cursor-pointer hover:bg-white transition-colors"
                    >
                      <i className="ri-bookmark-fill text-sm" />
                    </button>
                    <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-sans ${p.type === "video" ? "bg-teal/90 text-white" : "bg-gold/90 text-white"}`}>
                      {p.type === "video" ? "動画" : "キット"}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="font-sans text-sm text-charcoal font-medium line-clamp-2">{p.title}</p>
                    <p className="text-xs text-charcoal/40 font-sans mt-1">
                      {(p.provider as { service_name?: string; name?: string } | null)?.service_name || (p.provider as { service_name?: string; name?: string } | null)?.name || "事業者"}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="font-serif text-deep-green text-base">¥{p.price.toLocaleString()}</p>
                      <Link
                        to={`/products/${p.type}/${p.id}`}
                        className="text-xs text-teal font-sans cursor-pointer hover:underline whitespace-nowrap"
                      >
                        詳細を見る →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        providerBookmarks.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-100 py-16 text-center">
            <i className="ri-store-2-line text-5xl text-charcoal/20 block mb-4" />
            <p className="font-serif text-charcoal/40 text-lg">保存した事業者がありません</p>
            <Link to="/providers" className="mt-4 inline-block text-teal text-sm font-sans hover:underline cursor-pointer">事業者を探す →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {providerBookmarks.map((b) => {
              const p = b.provider;
              if (!p) return null;
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-stone-100 p-5 hover:-translate-y-1 transition-transform duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0">
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><i className="ri-store-2-line text-xl text-charcoal/30" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm text-charcoal font-medium truncate">{p.service_name || p.name}</p>
                      <p className="text-xs text-charcoal/40 font-sans truncate">{p.category || "事業者"}</p>
                      {p.rating && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-gold text-xs">★</span>
                          <span className="text-xs text-charcoal/60 font-sans">{p.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeBookmark(b.id, "providers")}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-100 text-gold cursor-pointer hover:bg-stone-200 transition-colors flex-shrink-0"
                    >
                      <i className="ri-bookmark-fill text-xs" />
                    </button>
                  </div>
                  <Link
                    to={`/providers/${p.id}`}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-full border border-teal text-teal text-xs font-sans cursor-pointer hover:bg-teal hover:text-white transition-colors whitespace-nowrap"
                  >
                    <i className="ri-store-2-line" />プロフィールを見る
                  </Link>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
